import React, { useState, useEffect, useRef } from 'react';
import { MoreVertical } from 'lucide-react';
import { Link } from 'react-router-dom';

export interface ActionMenuItem {
  label: string;
  icon: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  href?: string;
  className?: string;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
}

export function ActionMenu({ items }: ActionMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleItemClick = (e: React.MouseEvent, onClick?: (e: React.MouseEvent) => void) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick(e);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative z-50" ref={menuRef}>
      <button
        aria-label="Aktionsmenü"
        onClick={handleButtonClick}
        className="p-2 text-light/60 hover:text-light transition-colors rounded-full bg-dark md:bg-transparent hover:bg-gray-custom/10"
      >
        <MoreVertical className="w-5 h-5" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-dark border border-gray-custom/20 rounded-lg shadow-lg py-1 z-[60]">
          {items.map((item, index) => (
            item.href ? (
              <Link
                key={index}
                to={item.href}
                onClick={(e) => handleItemClick(e, item.onClick)}
                className={`w-full px-4 py-2 text-left text-sm text-light/60 hover:text-light hover:bg-gray-custom/10 flex items-center space-x-2 ${item.className || ''}`}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            ) : (
              <button
                key={index}
                onClick={(e) => handleItemClick(e, item.onClick)}
                className={`w-full px-4 py-2 text-left text-sm text-light/60 hover:text-light hover:bg-gray-custom/10 flex items-center space-x-2 ${item.className || ''}`}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            )
          ))}
        </div>
      )}
    </div>
  );
}